/**
 * The TOTAL-row math, shared by every evaluator. `mergeMetrics` sums the raw
 * additive keys (folding the sends/visits/registrations/ftd aliases into
 * their canonical names) and `toFullMetrics` re-derives the ratio metrics —
 * the same blended numbers the stats tables' TOTAL row shows. Moved here
 * verbatim from the SMSWiser performance-tag evaluator so `against:"total"`
 * comparisons resolve identically on the server, in rotation, and in the
 * browser preview.
 *
 * TRAP: never feed `toFullMetrics` a bag that already carries per-element
 * ratios when you want a blended total — `preferMetric` would keep them.
 * `totalMetrics` below does it right: sum raws first, derive once.
 * For Media Devoted | Created By Ciro Autiero
 */

import { deriveFunnelMetrics } from "../stats/derive-funnel-metrics";

export type MetricBag = Record<string, number>;

export const SUMMABLE_KEYS = [
  "impressions",
  "uniqueVisits",
  "uniqueClicks",
  "conversions",
  "customConversions6",
  "customConversions7",
  "revenue",
  "cost",
] as const;

function preferMetric(primary: number | undefined, fallback: number): number {
  if (typeof primary === "number" && Number.isFinite(primary)) return primary;
  return fallback;
}

export function mergeMetrics(acc: MetricBag, m: MetricBag): MetricBag {
  const out = { ...acc };
  for (const key of SUMMABLE_KEYS) {
    let val = m[key];
    if (key === "impressions" && val == null) val = m.sends;
    if (key === "uniqueVisits" && val == null) val = m.visits;
    if (key === "customConversions6" && val == null) val = m.registrations;
    /** ftd is an alias of customConversions7 from extractVoluumMetrics — do not add both. */
    if (key === "customConversions7" && val == null) val = m.ftd;
    if (typeof val === "number" && !Number.isNaN(val)) {
      out[key] = (out[key] ?? 0) + val;
    }
  }
  out.profit =
    (out.profit ?? 0) + (m.profit ?? (m.revenue ?? 0) - (m.cost ?? 0));
  return out;
}

export function toFullMetrics(m: MetricBag): MetricBag {
  const imp = m.impressions ?? m.sends ?? 0;
  const uv = m.uniqueVisits ?? 0;
  const uc = m.uniqueClicks ?? 0;
  const reg = m.customConversions6 ?? 0;
  const ftd = m.customConversions7 ?? 0;
  const conv = m.conversions ?? 0;
  const rev = m.revenue ?? 0;
  const cost = m.cost ?? 0;
  const profit = m.profit ?? rev - cost;
  return {
    ...m,
    profit,
    roi: cost > 0 ? (profit / cost) * 100 : 0,
    ImCTR: preferMetric(m.ImCTR, imp > 0 && uc > 0 ? (uc / imp) * 100 : 0),
    uCTR: preferMetric(m.uCTR, uv > 0 && uc > 0 ? (uc / uv) * 100 : 0),
    Click2Reg: preferMetric(
      m.Click2Reg,
      uc > 0 && reg > 0 ? (reg / uc) * 100 : 0,
    ),
    Reg2Dep: preferMetric(m.Reg2Dep, reg > 0 && ftd > 0 ? (ftd / reg) * 100 : 0),
    uEPC: preferMetric(m.uEPC, uc > 0 ? rev / uc : 0),
    uEPV: preferMetric(m.uEPV, uv > 0 ? rev / uv : 0),
    epv: preferMetric(m.epv, uv > 0 ? profit / uv : 0),
    epc: preferMetric(m.epc, uc > 0 ? profit / uc : 0),
    // EPL is REVENUE per CONVERSION (the trackers' own EPL basis — Voluum's
    // Conversions column / Binom's leads). Never the profit-based epc line
    // above, and NOT reg (customConversions6), which CPL divides by.
    epl: preferMetric(m.epl, conv > 0 ? rev / conv : 0),
    CPS: preferMetric(m.CPS, imp > 0 ? cost / imp : 0),
    CPuC: preferMetric(m.CPuC, uc > 0 ? cost / uc : 0),
    CPL: preferMetric(m.CPL, reg > 0 ? cost / reg : 0),
    ftd,
    visits: uv,
    registrations: reg,
  };
}

/**
 * The blended TOTAL across a population: raws summed, ratios derived once.
 * `undefined` when NO bag contributed — an empty population must fail
 * closed, never read as a total of zeros.
 */
export function totalMetrics(
  bags: Iterable<MetricBag | null | undefined>,
): MetricBag | undefined {
  let acc: MetricBag = {};
  let contributed = 0;
  for (const bag of bags) {
    if (!bag || Object.keys(bag).length === 0) continue;
    acc = mergeMetrics(acc, bag);
    contributed += 1;
  }
  return contributed > 0 ? toFullMetrics(acc) : undefined;
}

/* ------------------------------------------------------------------ */
/* Flow-Optimizer vocabulary (Voluum-style ids: visits ≠ uniqueVisits) */
/* ------------------------------------------------------------------ */

export const FLOW_ADDITIVE_KEYS = [
  "impressions",
  "visits",
  "uniqueVisits",
  "clicks",
  "uniqueClicks",
  "conversions",
  "customConversions6",
  "customConversions7",
  "registrations",
  "ftd",
  "revenue",
  "cost",
] as const;

const num = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : 0;
};

const div = (a: number, b: number): number => (b ? a / b : 0);

const FLOW_COUNTER_ALIASES: Record<string, string> = {
  customConversions6: "registrations", customConversions7: "ftd",
  registrations: "customConversions6", ftd: "customConversions7",
};

export function mergeFlowMetrics(acc: MetricBag, m: MetricBag): MetricBag {
  for (const key of FLOW_ADDITIVE_KEYS) {
    acc[key] = num(acc[key]) + num(m[key] ?? m[FLOW_COUNTER_ALIASES[key]]);
  }
  // Per-row fallback like the SMSWiser merge: a row without an explicit
  // profit still contributes revenue − cost instead of zeroing the total.
  acc.profit =
    num(acc.profit) + ("profit" in m ? num(m.profit) : num(m.revenue) - num(m.cost));
  return acc;
}

/**
 * Re-derive every FO ratio from aggregated raws — the same formulas the
 * cube's own aggregation uses (voluum-metrics-aggregate.util delegates
 * here), plus the advanced set so EVERY selectable metric has a TOTAL.
 */
export function deriveFlowMetrics(base: MetricBag): MetricBag {
  const impressions = num(base.impressions);
  const visits = num(base.visits);
  const clicks = num(base.clicks);
  const uniqueVisits = num(base.uniqueVisits);
  const uniqueClicks = num(base.uniqueClicks);
  const conversions = num(base.conversions);
  const revenue = num(base.revenue);
  const cost = num(base.cost);
  const profit = "profit" in base ? num(base.profit) : revenue - cost;

  base.profit = profit;
  base.roi = div(profit, cost) * 100;
  base.ctr = div(clicks, visits) * 100;
  base.cr = div(conversions, visits) * 100;
  base.cv = div(conversions, clicks) * 100;
  base.epv = div(revenue, visits);
  base.epc = div(revenue, clicks);
  base.uEPC = div(revenue, uniqueClicks);
  base.uctr = div(uniqueClicks, uniqueVisits) * 100;
  base.ucr = div(conversions, uniqueClicks) * 100;
  base.cpv = div(cost, visits);
  base.cpc = div(cost, clicks);
  base.ecpa = div(cost, conversions);
  base.ecpm = div(cost, impressions) * 1000;
  base.rpm = div(revenue, visits) * 1000;
  base.impressions = impressions;
  Object.assign(base, deriveFunnelMetrics(base));
  return base;
}

/** Blended FO TOTAL; undefined on an empty population (fail-closed). */
export function totalFlowMetrics(
  bags: Iterable<MetricBag | null | undefined>,
): MetricBag | undefined {
  const acc: MetricBag = {};
  let contributed = 0;
  for (const bag of bags) {
    if (!bag || Object.keys(bag).length === 0) continue;
    mergeFlowMetrics(acc, bag);
    contributed += 1;
  }
  return contributed > 0 ? deriveFlowMetrics(acc) : undefined;
}
